import React, { useState, useEffect, useRef } from 'react';
import './RecetaModal.css';
import FormModal from '../common/FormModal';
import Select from 'react-select';
import { FaPlus, FaTrashAlt, FaSave } from 'react-icons/fa';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderColor: state.isFocused ? '#FF9800' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 152, 0, 0.1)' : 'none',
    '&:hover': { borderColor: '#FF9800' }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#FF9800' : state.isFocused ? '#FFF3E0' : 'white',
    color: state.isSelected ? 'white' : '#333',
    fontSize: '13px'
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  placeholder: (base) => ({ ...base, fontSize: '13px' }),
  singleValue: (base) => ({ ...base, fontSize: '13px' })
};

const RecetaModal = ({ isOpen, onClose, platillo }) => {
  const [recetaItems, setRecetaItems] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [preparados, setPreparados] = useState([]);
  const [tipoItem, setTipoItem] = useState('ingrediente');
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [cantidadNueva, setCantidadNueva] = useState('');
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const cantidadRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen && platillo) {
      cargarDatos();
    }
    if (!isOpen) {
      setRecetaItems([]);
      setItemSeleccionado(null);
      setCantidadNueva('');
      setTipoItem('ingrediente');
    }
  }, [isOpen, platillo]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [resReceta, resIngredientes, resPreparados] = await Promise.all([
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getRecetaPlatillo', idPlatillo: platillo.idProducto })
        }),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getIngredientes' })
        }),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getPreparados' })
        })
      ]);

      const dataReceta = await resReceta.json();
      const dataIngredientes = await resIngredientes.json();
      const dataPreparados = await resPreparados.json();

      if (dataReceta.success) {
        setRecetaItems(dataReceta.data || []);
      }
      if (dataIngredientes.success) {
        setIngredientes(dataIngredientes.data || []);
      }
      if (dataPreparados.success) {
        setPreparados(dataPreparados.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos de receta:', error);
      toast.error('Error al cargar datos de la receta');
    } finally {
      setLoading(false);
    }
  };

  const opcionesSelect = tipoItem === 'ingrediente'
    ? ingredientes.map(i => ({ value: i.idIngrediente, label: i.nombre }))
    : preparados.map(p => ({ value: p.idPreparado, label: p.nombre }));

  const agregarItem = () => {
    if (!itemSeleccionado) {
      toast.error('Seleccione un item');
      return;
    }
    const cantidad = parseFloat(cantidadNueva);
    if (!cantidad || cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    // Si ya existe, sumar la cantidad
    const idBuscado = Number(itemSeleccionado.value);
    const indexExistente = recetaItems.findIndex(item =>
      tipoItem === 'ingrediente'
        ? Number(item.idIngrediente) === idBuscado && Number(item.idIngrediente) > 0
        : Number(item.idPreparado) === idBuscado && Number(item.idPreparado) > 0
    );

    if (indexExistente >= 0) {
      setRecetaItems(prev => prev.map((item, i) =>
        i === indexExistente ? { ...item, cantidad: item.cantidad + cantidad } : item
      ));
    } else {
      const nuevoItem = {
        idIngrediente: tipoItem === 'ingrediente' ? itemSeleccionado.value : 0,
        idPreparado: tipoItem === 'preparado' ? itemSeleccionado.value : 0,
        cantidad: cantidad,
        nombre: itemSeleccionado.label,
        tipo: tipoItem
      };
      setRecetaItems(prev => [...prev, nuevoItem]);
    }
    setItemSeleccionado(null);
    setCantidadNueva('');
  };

  const eliminarItem = (index) => {
    setRecetaItems(prev => prev.filter((_, i) => i !== index));
  };

  const actualizarCantidad = (index, nuevaCantidad) => {
    const valor = parseFloat(nuevaCantidad);
    if (isNaN(valor)) return;
    setRecetaItems(prev => prev.map((item, i) =>
      i === index ? { ...item, cantidad: valor } : item
    ));
  };

  const guardarReceta = async () => {
    try {
      setGuardando(true);

      const itemsParaGuardar = recetaItems.map(item => ({
        idIngrediente: item.idIngrediente || 0,
        idPreparado: item.idPreparado || 0,
        cantidad: item.cantidad
      }));

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'guardarRecetaPlatillo',
          idPlatillo: platillo.idProducto,
          items: itemsParaGuardar
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Receta guardada correctamente');
        onClose();
      } else {
        toast.error(data.message || 'Error al guardar receta');
      }
    } catch (error) {
      console.error('Error al guardar receta:', error);
      toast.error('Error al guardar receta');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Receta: ${platillo?.nombre || ''}`}
      onSave={guardarReceta}
      saveLabel="Guardar Receta"
      isSaving={guardando}
      size="large"
      className="receta-modal-wide"
      saveIcon={<FaSave />}
    >
      {loading ? (
        <div className="receta-loading">Cargando receta...</div>
      ) : (
        <>
          {/* Seccion agregar item */}
          <div className="receta-agregar-row">
            <div className="receta-tipo-selector">
              <button
                type="button"
                className={`receta-tipo-btn ${tipoItem === 'ingrediente' ? 'active' : ''}`}
                onClick={() => { setTipoItem('ingrediente'); setItemSeleccionado(null); }}
              >
                Ingrediente
              </button>
              <button
                type="button"
                className={`receta-tipo-btn ${tipoItem === 'preparado' ? 'active' : ''}`}
                onClick={() => { setTipoItem('preparado'); setItemSeleccionado(null); }}
              >
                Preparado
              </button>
            </div>

            <div className="receta-select-container">
              <Select
                key={tipoItem}
                value={itemSeleccionado}
                onChange={(option) => { setItemSeleccionado(option); if (option) cantidadRef.current?.focus(); }}
                options={opcionesSelect}
                placeholder={tipoItem === 'ingrediente' ? 'Buscar ingrediente...' : 'Buscar preparado...'}
                isSearchable={true}
                isClearable={true}
                noOptionsMessage={() => "Sin resultados"}
                styles={selectStyles}
                menuPortalTarget={document.body}
              />
            </div>

            <input
              ref={cantidadRef}
              type="number"
              className="receta-cantidad-input"
              placeholder="Cantidad"
              value={cantidadNueva}
              onChange={(e) => setCantidadNueva(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarItem(); } }}
              step="0.001"
              min="0"
            />

            <button
              type="button"
              className="receta-btn-agregar"
              onClick={agregarItem}
              disabled={!itemSeleccionado || !cantidadNueva}
            >
              <FaPlus /> Agregar
            </button>
          </div>

          {/* Tabla de items */}
          <div className="receta-tabla-container">
            {recetaItems.length === 0 ? (
              <div className="receta-sin-items">No hay items en la receta. Agregue ingredientes o preparados.</div>
            ) : (
              <table className="receta-tabla">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Nombre</th>
                    <th>Cantidad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {recetaItems.map((item, index) => (
                    <tr key={`${item.tipo}-${item.idIngrediente || item.idPreparado}-${index}`}>
                      <td>
                        <span className={`receta-badge ${item.tipo === 'ingrediente' ? 'receta-badge-ingrediente' : 'receta-badge-preparado'}`}>
                          {item.tipo === 'ingrediente' ? 'Ingrediente' : 'Preparado'}
                        </span>
                      </td>
                      <td>{item.nombre}</td>
                      <td>
                        <input
                          type="number"
                          className="receta-cantidad-inline"
                          value={item.cantidad}
                          onChange={(e) => actualizarCantidad(index, e.target.value)}
                          step="0.001"
                          min="0"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="receta-btn-eliminar"
                          onClick={() => eliminarItem(index)}
                          title="Eliminar item"
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </FormModal>
  );
};

export default RecetaModal;
